import React, { memo, useCallback, useMemo, useState } from 'react';
import { Checkbox, Input, Menu, Table, Tooltip } from 'antd';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import NameCell from './components/TableCells/NameCell';
import ActionCell from './components/TableCells/ActionCell';
import DecisionsCell from './components/TableCells/DecisionsCell';
import CommentsCell from './components/TableCells/CommentsCell';
import { ApprovalWorkflowItemDto, AWAssetDto, AWStageDto } from '@api/Api';
import PageSortPanel from '@components/PageSortPanel';
import { useDebounced } from '@helpers/useDebounced';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { sortedAWListAssetsToReviewInBatch } from '@components/Modals/ApprovalModal/utils';
import { toggleApprovalAssetModal } from '@redux/actions/modalAction';
import ApprovalBulkManagement from '@components/Approval/ApprovalBulkActions/ApprovalBulkManagement';
import Status from '@components/Approval/Status';
import StatusesFilter from '@components/Approval/StatusesFilter/StatusesFilter';
import { QUERY_APPROVAL_APPROVAL_WORKFLOW } from '@hooks/queries/approval';
import LottieComponent from '@components/Lottie';
import { DEMO_TOUR_APPROVAL_WORKFLOW_ID } from '@config/constants';
import { SearchIcon } from '@icons';

import './CampaignApprovalContent.less';

type CampaignApprovalContentProps = {
  approvalWorkflow: ApprovalWorkflowItemDto;
  currentStage: AWStageDto | null;
  isPastDueDate: boolean;
};

export default memo(function CampaignApprovalContent({
  approvalWorkflow,
  isPastDueDate,
  currentStage
}: CampaignApprovalContentProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { tourData } = useOnboardingProcess();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAssets, setSelectedAssets] = useState<AWAssetDto[]>([]);
  const sortByParam = searchParams.get('sortBy') || 'needReview';
  const statusesParam = searchParams.getAll('statuses');
  const searchParam = searchParams.get('search');
  const searchText = useDebounced(searchParam, 600);

  const dataSource = useMemo(() => {
    if (!approvalWorkflow.assets?.length) return [];
    const hasActiveTour =
      tourData?.type === 'route-for-approval' &&
      approvalWorkflow.id === DEMO_TOUR_APPROVAL_WORKFLOW_ID;
    let { assets } = approvalWorkflow;

    if (hasActiveTour) assets = assets.slice(0, 1);
    if (searchText) {
      assets = assets.filter((asset) =>
        asset.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())
      );
    }
    if (statusesParam.length) {
      assets = assets.filter((asset) => {
        const { status } = asset;
        return (
          statusesParam.includes(status || '') ||
          (status === 'pending' && statusesParam.includes('in_progress'))
        );
      });
    }
    return sortedAWListAssetsToReviewInBatch(assets, sortByParam);
  }, [
    approvalWorkflow.id,
    approvalWorkflow.assets,
    currentStage?.id,
    sortByParam,
    searchText,
    statusesParam,
    tourData?.type
  ]);

  const onChangeSelectedAssets = useCallback(
    (asset: AWAssetDto) => {
      setSelectedAssets((prevSelectedAssets) => {
        const existAsset = prevSelectedAssets.find(
          (item) => item.id === asset.id
        );
        if (existAsset)
          return prevSelectedAssets.filter((item) => item.id !== asset.id);
        return [...prevSelectedAssets, asset];
      });
    },
    [setSelectedAssets]
  );

  const isAllSelectedAssets =
    selectedAssets.length === approvalWorkflow.assets?.length;

  const onOpenAsset = useCallback(
    (asset: AWAssetDto) => {
      const assetUrl = asset.hasAccess
        ? `/media-viewer/${asset.id}/${asset.versionId}`
        : `/approval?id=${approvalWorkflow.id}&assetId=${asset.id}`;
      navigate(assetUrl, {
        state: {
          returnTo: location.pathname + location.search
        }
      });
    },
    [approvalWorkflow.id, location.pathname, location.search, navigate]
  );

  const columns = useMemo(
    () => [
      {
        title: (
          <div className="campaign-approval-table_head-name">
            <Checkbox
              disabled={!approvalWorkflow.permissions.manage}
              checked={isAllSelectedAssets}
              onChange={() =>
                isAllSelectedAssets
                  ? setSelectedAssets([])
                  : setSelectedAssets(approvalWorkflow.assets || [])
              }
              onClick={(e) => e.stopPropagation()}
            />
            Media
          </div>
        ),
        dataIndex: 'name',
        key: 'name',
        width: 420,
        render: (_name: string, asset: AWAssetDto) => {
          const isSelected = selectedAssets.some(
            (item) => item.id === asset.id
          );
          return (
            <NameCell
              asset={asset}
              isDisabled={!approvalWorkflow.permissions.manage}
              isSelected={isSelected}
              onClick={
                asset.accessDisabled ? undefined : () => onOpenAsset(asset)
              }
              onSelect={onChangeSelectedAssets}
            />
          );
        }
      },
      {
        title: 'Stage',
        dataIndex: 'statusStageId',
        key: 'stage',
        width: 20 + approvalWorkflow.stages.length * 43,
        render: (statusStageId: string | null, asset: AWAssetDto) => {
          const stage = approvalWorkflow.stages.find(
            (x) => x.id === statusStageId
          );
          return (
            <div className="campaign-approval_info-progress">
              {!stage && '-'}
              {!!stage && (
                <div
                  className={classNames(
                    'campaign-approval_info-progress-lines',
                    { 'b-lines--pause': approvalWorkflow.isPaused }
                  )}
                >
                  {approvalWorkflow.stages.map((item, index) => {
                    const stageStatus = asset.statuses?.find(
                      (x) => x.stageId === item.id
                    );
                    return (
                      <Tooltip
                        title={
                          <>
                            <div className="campaign-approval_info-progress-tooltip_label">
                              Stage: {index + 1}/
                              {approvalWorkflow.stages.length}
                            </div>
                            <div className="campaign-approval_info-progress-tooltip_text">
                              {item.name}
                            </div>
                          </>
                        }
                        overlayClassName="toggle_favorite_overlay campaign-approval_info-progress-tooltip"
                        key={item.id}
                        overlayInnerStyle={{
                          maxWidth: 550
                        }}
                      >
                        <div
                          className={classNames(
                            'campaign-approval_info-progress-line',
                            {
                              'b-line--active': item.id === stage?.id,
                              'b-line--finished': !!stageStatus?.isFinished,
                              'b-line--approved':
                                stageStatus?.status === 'approved',
                              'b-line--approved-with-feedback':
                                stageStatus?.status ===
                                'approved_with_feedback',
                              'b-line--changes-required':
                                stageStatus?.status === 'changes_required',
                              'b-line--overdue':
                                isPastDueDate && item.id === stage?.id
                            }
                          )}
                        />
                      </Tooltip>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 175,
        render: (status: AWAssetDto['status']) => {
          return <Status status={status} deadline={currentStage?.deadline} />;
        }
      },
      {
        title: 'Reviewers',
        dataIndex: 'statusStageId',
        key: 'reviewers',
        width: 230,
        render: (statusStageId: string | null, asset: AWAssetDto) => {
          const stage = approvalWorkflow.stages.find(
            (x) => x.id === statusStageId
          );
          return (
            <DecisionsCell
              assetVersionId={asset.versionId}
              isPastDueDate={isPastDueDate}
              members={stage?.members || []}
            />
          );
        }
      },
      {
        title: 'Comments',
        dataIndex: 'versionId',
        key: 'versionId',
        width: 60,
        render: (versionId: string, asset: AWAssetDto) => {
          return (
            <CommentsCell
              commentsCount={asset.commentsCount}
              onClick={() => onOpenAsset(asset)}
            />
          );
        }
      },
      {
        title: '',
        dataIndex: 'versionId',
        key: 'versionId',
        render: (versionId: string, asset: AWAssetDto) => {
          return (
            <div style={{ width: 24, marginLeft: 'auto' }}>
              <ActionCell approval={approvalWorkflow} assetId={asset.id} />
            </div>
          );
        }
      }
    ],
    [
      isAllSelectedAssets,
      approvalWorkflow,
      currentStage,
      isPastDueDate,
      selectedAssets,
      onOpenAsset,
      onChangeSelectedAssets
    ]
  );

  return (
    <div className="campaign-approval_content">
      <div className="campaign-approval_content-head">
        <StatusesFilter
          onChange={(statuses) => {
            searchParams.delete('statuses');
            if (statuses.length) {
              statuses.forEach((status) =>
                searchParams.append('statuses', status)
              );
            }
            setSearchParams(searchParams);
          }}
          selectedStatuses={statusesParam}
        />
        <PageSortPanel
          isVisibleSortStyle={false}
          classNamePanel="campaign-approval_content-sort"
          entity="approvals"
          sortMenuModal={
            <Menu
              selectedKeys={[sortByParam]}
              style={{
                minWidth: 260
              }}
              onClick={({ key }) => {
                searchParams.set('sortBy', key);
                setSearchParams(searchParams);
              }}
              items={[
                { label: 'Need to review', key: 'needReview' },
                { label: 'Already completed review', key: 'completedReview' }
              ]}
            />
          }
          searchInputComponent={
            <Input
              style={{ marginRight: 16, padding: '8px 12px' }}
              placeholder="Search"
              prefix={<SearchIcon />}
              size="large"
              defaultValue={searchParam || ''}
              onChange={(e) => {
                const _searchText = e.target.value.trim();
                if (_searchText) searchParams.set('search', _searchText);
                if (!_searchText) searchParams.delete('search');
                setSearchParams(searchParams);
              }}
            />
          }
        />
      </div>
      <Table
        className="campaign-approval_content-table"
        columns={columns}
        dataSource={[...dataSource]}
        onRow={(asset) => {
          return {
            onClick: () => {
              dispatch(
                toggleApprovalAssetModal({
                  visible: true,
                  assetId: asset.id,
                  versionId: asset.versionId,
                  approvalWorkflowId: approvalWorkflow.id,
                  callback: (aw) => {
                    if (!aw) {
                      navigate(
                        `/campaigns/single/${approvalWorkflow.campaignId}?tab=approvals`
                      );
                    }
                  }
                })
              );
            }
          };
        }}
        rowKey="id"
        tableLayout="fixed"
        pagination={false}
        locale={{
          emptyText: !dataSource.length ? (
            <div className="empty_state empty_state--search">
              <LottieComponent className="icon" view="noSearchingResult" />
              <div
                className="main-body-text main-body-text--bold"
                style={{ marginBottom: 8 }}
              >
                No results found
              </div>
              <p className="copy main-body-text main-body-text--dark-gray">
                It seems we can’t find any results based on your search
              </p>
            </div>
          ) : null
        }}
      />
      <ApprovalBulkManagement
        approvalWorkflow={approvalWorkflow}
        assets={selectedAssets}
        onClose={() => setSelectedAssets([])}
        queryKey={[QUERY_APPROVAL_APPROVAL_WORKFLOW]}
      />
    </div>
  );
});
